// Constant-product (x*y=k) AMM math, matching UniswapV2Library exactly (0.3%
// fee, 997/1000) — this is what DotOneSwapV2Router02.getAmountsOut computes
// on-chain, so quoting it client-side from cached reserves (see
// data/v2/pools.ts) needs no RPC round trip per keystroke.

export function getAmountOut(amountIn: bigint, reserveIn: bigint, reserveOut: bigint): bigint {
  if (amountIn <= 0n) throw new Error("INSUFFICIENT_INPUT_AMOUNT");
  if (reserveIn <= 0n || reserveOut <= 0n) throw new Error("INSUFFICIENT_LIQUIDITY");
  const amountInWithFee = amountIn * 997n;
  const numerator = amountInWithFee * reserveOut;
  const denominator = reserveIn * 1000n + amountInWithFee;
  return numerator / denominator;
}

export function getAmountIn(amountOut: bigint, reserveIn: bigint, reserveOut: bigint): bigint {
  if (amountOut <= 0n) throw new Error("INSUFFICIENT_OUTPUT_AMOUNT");
  if (reserveIn <= 0n || reserveOut <= 0n) throw new Error("INSUFFICIENT_LIQUIDITY");
  if (amountOut >= reserveOut) throw new Error("INSUFFICIENT_LIQUIDITY");
  const numerator = reserveIn * amountOut * 1000n;
  const denominator = (reserveOut - amountOut) * 997n;
  return numerator / denominator + 1n;
}

// Proportional deposit amount for adding liquidity at the pool's current
// ratio (no fee applied — this is the reserve-ratio quote, not a swap).
export function quote(amountA: bigint, reserveA: bigint, reserveB: bigint): bigint {
  if (amountA <= 0n) throw new Error("INSUFFICIENT_AMOUNT");
  if (reserveA <= 0n || reserveB <= 0n) throw new Error("INSUFFICIENT_LIQUIDITY");
  return (amountA * reserveB) / reserveA;
}

// Percent difference between the pool's mid price and this trade's actual
// execution price — the standard "price impact" shown next to a swap quote.
export function priceImpactPct(amountIn: bigint, amountOut: bigint, reserveIn: bigint, reserveOut: bigint): number {
  if (reserveIn === 0n || reserveOut === 0n || amountIn === 0n) return 0;
  const midPrice = Number(reserveOut) / Number(reserveIn);
  if (midPrice === 0) return 0;
  const execPrice = Number(amountOut) / Number(amountIn);
  return Math.max(0, (1 - execPrice / midPrice) * 100);
}
